import { Head, Link, useForm } from '@inertiajs/react'
import { HomeMark } from '../../shared/HomeMark.jsx'
import { withSiteFooter } from '../../shared/SiteFooter.jsx'
import { Button } from '../../components/core/Button.jsx'
import { Field } from '../../components/forms/Field.jsx'
import { Input } from '../../components/forms/Input.jsx'
import { Alert } from '../../components/feedback/Alert.jsx'

// The response is identical whether or not the address has an account here, so
// there is no "we could not find that email" state to draw.

export default function ResetRequest({ errors = {} }) {
  const form = useForm({ email: '' })

  function submit(e) {
    e.preventDefault()
    form.post('/passwords')
  }

  return (
    <>
      <Head title="Reset your password" />
      <main className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center gap-8 px-6 py-16">
        <HomeMark />
        <div className="flex flex-col gap-2">
          <h1 className="page-title">Reset your password</h1>
          <p className="text-[15px] text-[color:var(--text-body)]">
            Enter the address you signed up with and we will send a link to set a new password.
          </p>
        </div>

        {/* Re-applied on integration. `PasswordsController` writes its rate limit and "that reset link has expired or has already been used" to
            `errors.base`, and no field on this page reads that key — so the
            failure returned a clean redirect and the page said **nothing at
            all**. An error nobody prints is the quiet half of the one-flat-bag
            problem in CLAUDE.md, and on sign-in it landed on the most-used form
            in the product. Verified in a browser 2026-09-24: the message is in
            the props and the page never renders it. */}
        {errors.base && (
          <Alert tone="signal" icon="alert-circle">
            {errors.base}
          </Alert>
        )}

        <form onSubmit={submit} className="flex flex-col gap-5">
          {/*
            The email field carries no hint, deliberately. It read "The same
            reply is sent either way, which keeps this form from revealing who
            has an account here" — removed 2026-10-06.

            **The behaviour it described is right and is unchanged.** What was
            wrong is narrating it: a reader who had not thought about enumeration
            is now picturing this form being used against them, on a page they
            reached because they are already locked out. Explaining a defence
            raises the threat it defends against.

            The heading above already carries the whole instruction — "Enter the
            address you signed up with and we will send a link to set a new
            password" — so nothing is lost by saying less.
          */}
          <Field
            label="Email"
            htmlFor="email"
            error={errors.email}
          >
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              value={form.data.email}
              invalid={Boolean(errors.email)}
              onChange={(e) => form.setData('email', e.target.value)}
            />
          </Field>

          <Button variant="primary" size="lg" block as="button" loading={form.processing}>
            Send the link
          </Button>
        </form>

        <p className="text-[13.5px] text-[color:var(--text-muted)]">
          <Link href="/session/new" className="underline underline-offset-4">
            Back to sign in
          </Link>
        </p>
      </main>
    </>
  )
}

ResetRequest.layout = withSiteFooter
